import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import {
  createRouter,
  createRoute,
  createRootRoute,
  RouterProvider,
} from '@tanstack/react-router'
import './index.css'
import App from './App.tsx'
import { initSentry } from './sentry'
import { queryClient } from './lib/queryClient'
import { HomePage } from './features/home/HomePage'
import { ThesesListPage } from './features/theses/ThesesListPage'
import { ThesisDetailPage } from './features/theses/ThesisDetailPage'
import { RunDetailPage } from './features/agents/RunDetailPage'
import { TradesPage } from './features/trades/TradesPage'
import { DataLakePage } from './features/datalake/DataLakePage'
import { DataSourceDetailPage } from './features/datalake/DataSourceDetailPage'
import { ModelsPage } from './features/models/ModelsPage'
import { MeritOrderPage } from './features/models/MeritOrderPage'
import { AgentsPage } from './features/agents/AgentsPage'
import { AgentDetailPage } from './features/agents/AgentDetailPage'
import { ScriptDetailPage } from './features/theses/ScriptDetailPage'
import { ScriptRunDetailPage } from './features/theses/ScriptRunDetailPage'
import { AssistantPage } from './features/chat/AssistantPage'
import { ArtifactDetailPage } from './features/chat/ArtifactDetailPage'
import { validateConversationSearch } from './features/chat/conversationSearch'
import { SettingsPage } from './features/settings/SettingsPage'
import { PersonalSkillsPage } from './features/skills/PersonalSkillsPage'
import { ProfilePage } from './features/profile/ProfilePage'

function isResizeObserverLoopMessage(message: unknown): boolean {
  return typeof message === 'string' && message.includes('ResizeObserver loop')
}

window.addEventListener('error', (e) => {
  if (isResizeObserverLoopMessage(e.message)) {
    e.stopImmediatePropagation()
    e.preventDefault()
  }
}, true)

window.addEventListener('unhandledrejection', (e) => {
  if (isResizeObserverLoopMessage(e.reason?.message ?? e.reason)) {
    e.stopImmediatePropagation()
    e.preventDefault()
  }
}, true)

initSentry()

const rootRoute = createRootRoute()

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: App,
})

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage })
const thesesRoute = createRoute({ getParentRoute: () => appRoute, path: 'theses', component: ThesesListPage })
const thesisDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'theses/$thesisId',
  component: ThesisDetailPage,
})
const runDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'agents/runs/$runId',
  component: RunDetailPage,
})
const tradesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'trades',
  component: TradesPage,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'positions' | 'executions' | 'pnl' | 'runs' | 'scripts' | 'old-orders' | 'old-exposure' | 'old-pnl' | 'orders' | 'fills'
    thesis?: string[]
    product?: string[]
    status?: string[]
    contract?: string
    from?: string
    to?: string
    errored_only?: boolean
    enabled_only?: boolean
    graduated_only?: boolean
    view?: string
  } => {
    const arr = (v: unknown): string[] | undefined => {
      if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string')
      if (typeof v === 'string') {
        if (v.trim().startsWith('[')) {
          try {
            const parsed: unknown = JSON.parse(v)
            if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string')
          } catch {
            return [v]
          }
        }
        return [v]
      }
      return undefined
    }
    const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
    const tab =
      s.tab === 'positions' ||
      s.tab === 'executions' ||
      s.tab === 'pnl' ||
      s.tab === 'runs' ||
      s.tab === 'scripts' ||
      s.tab === 'old-orders' ||
      s.tab === 'old-exposure' ||
      s.tab === 'old-pnl' ||
      s.tab === 'orders' ||
      s.tab === 'fills'
        ? s.tab
        : undefined
    return {
      tab,
      thesis: arr(s.thesis),
      product: arr(s.product),
      status: arr(s.status),
      contract: str(s.contract),
      from: str(s.from),
      to: str(s.to),
      errored_only: s.errored_only === true || s.errored_only === 'true' ? true : undefined,
      enabled_only: s.enabled_only === true || s.enabled_only === 'true' ? true : undefined,
      graduated_only: s.graduated_only === true || s.graduated_only === 'true' ? true : undefined,
      view: str(s.view),
    }
  },
})
const scriptDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'theses/$thesisId/scripts/$scriptId',
  component: ScriptDetailPage,
})
const scriptRunDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'theses/$thesisId/script-runs/$scriptRunId',
  component: ScriptRunDetailPage,
})
const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'agents',
  component: AgentsPage,
  validateSearch: (s: Record<string, unknown>): { tab?: 'events' | 'agents' | 'runs' | 'schedule' | 'ledger' | 'loops' | 'skills' } => ({
    tab:
      s.tab === 'agents' ||
      s.tab === 'runs' ||
      s.tab === 'schedule' ||
      s.tab === 'events' ||
      s.tab === 'ledger' ||
      s.tab === 'loops' ||
      s.tab === 'skills'
        ? s.tab
        : undefined,
  }),
})
const agentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'agents/$agentName',
  component: AgentDetailPage,
})
const dataRoute = createRoute({ getParentRoute: () => appRoute, path: 'data', component: DataLakePage })
const dataDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'data/$name',
  component: DataSourceDetailPage,
})
const assistantRoute = createRoute({
  getParentRoute: () => appRoute, path: 'assistant', component: AssistantPage,
  validateSearch: validateConversationSearch,
})
const assistantConvRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'assistant/$conversationId',
  component: AssistantPage,
  validateSearch: validateConversationSearch,
})
const artifactDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'artifacts/$kind/$id',
  component: ArtifactDetailPage,
})
const modelsRoute = createRoute({ getParentRoute: () => appRoute, path: 'models', component: ModelsPage })
const meritOrderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'models/merit-order',
  component: MeritOrderPage,
})
const organizationRoute = createRoute({ getParentRoute: () => appRoute, path: 'organization', component: SettingsPage })
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: 'settings', component: SettingsPage })
const skillsRoute = createRoute({ getParentRoute: () => appRoute, path: 'skills', component: PersonalSkillsPage })
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: 'profile', component: ProfilePage })

const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    homeRoute,
    thesesRoute,
    thesisDetailRoute,
    runDetailRoute,
    scriptDetailRoute,
    scriptRunDetailRoute,
    agentsRoute,
    agentDetailRoute,
    tradesRoute,
    dataRoute,
    dataDetailRoute,
    assistantRoute,
    assistantConvRoute,
    artifactDetailRoute,
    modelsRoute,
    meritOrderRoute,
    skillsRoute,
    profileRoute,
    organizationRoute,
    settingsRoute,
  ]),
])

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
